import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, Button, List, ListItem, ListItemText, Checkbox, IconButton,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Paper
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { enhancedApi } from '../api';

const PAGE_SIZE = 5;

const ClientAssignments = ({ userId, open, onClose }) => {
  const [assignedClients, setAssignedClients] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [searchAssigned, setSearchAssigned] = useState('');
  const [searchAll, setSearchAll] = useState('');
  const [showAllClients, setShowAllClients] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [loadingAssigned, setLoadingAssigned] = useState(true);
  const [loadingAllClients, setLoadingAllClients] = useState(false);
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');
  const [page, setPage] = useState(1);
  const justClosedRef = useRef(false);

  useEffect(() => {
    if (open) {
      fetchAssignedClients();
      fetchUserName();
    }
  }, [open, userId]);

  const fetchUserName = async () => {
    try {
      const res = await enhancedApi.getUser(userId);
      let userData = res.data;
      if (Array.isArray(userData)) {
        userData = userData.find(user => user.id === parseInt(userId)) || {};
      }
      setUserName(userData.first_name ? `${userData.first_name} ${userData.last_name || ''}` : userData.username || '');
    } catch (error) {
      console.error('Error fetching user name:', error);
      setUserName('');
    }
  };

  const fetchAssignedClients = async () => {
    setLoadingAssigned(true);
    try {
      const response = await enhancedApi.getClientAssignments(userId);
      const data = response?.data;
      setAssignedClients(Array.isArray(data) ? data : (data?.assigned_clients || []));
      setError('');
    } catch (error) {
      console.error('Error fetching assigned clients:', error);
      setError('Failed to load assigned clients');
    } finally {
      setLoadingAssigned(false);
    }
  };

  const handleSearchClients = async () => {
    setShowAllClients(true);
    setLoadingAllClients(true);
    try {
      const response = await enhancedApi.getAllClients();
      setAllClients(response.data || []);
      setError('');
    } catch (error) {
      console.error('Error fetching all clients:', error);
      setError('Failed to load clients');
    } finally {
      setLoadingAllClients(false);
    }
  };



  const filteredAssignedClients = assignedClients.filter(client =>
    (client.display_name || client.primary_contact?.display_name || 'Unknown Client').toLowerCase().includes(searchAssigned.toLowerCase())
  );

  const filteredAllClients = allClients.filter(client =>

    (client.display_name || 'Unknown Client').toLowerCase().includes(searchAll.toLowerCase()) &&
    !assignedClients.some(ac => ac.id === client.id)
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredAssignedClients.length / PAGE_SIZE) || 1;
  const pagedAssignedClients = filteredAssignedClients.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleToggleClient = (clientId) => {
    setSelectedClients(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleAssignClients = async () => {
    try {
      await enhancedApi.assignClientsToUser(userId, {
        client_ids: selectedClients,
        action: 'assign',
      });
      fetchAssignedClients();
      setSelectedClients([]);
      setShowAllClients(false);
    } catch (error) {
      setError('Failed to assign clients');
    }
  };

  const handleUnassignClient = async (clientId) => {
    try {
      await enhancedApi.assignClientsToUser(userId, {
        client_ids: [clientId],
        action: 'unassign',
      });
      fetchAssignedClients();
    } catch (error) {
      setError('Failed to unassign client');
    }
  };

  // Prevent dialog from reopening immediately after close
  const handleCloseAllClients = () => {
    setShowAllClients(false);
    justClosedRef.current = true;
    setTimeout(() => { justClosedRef.current = false; }, 200);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 'bold', fontSize: 22, pb: 0, pt: 3, px: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Assign User To Clients</span>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 1, pb: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 1 }}>{userName}</Typography>
        <Paper variant="outlined" sx={{ p: 0, mb: 2, boxShadow: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Clients</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                placeholder="Search assigned clients"
                value={searchAssigned}
                onChange={e => setSearchAssigned(e.target.value)}
                size="small"
                sx={{ minWidth: 220, background: '#f5f5f5', borderRadius: 1 }}
                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
              />
              <TextField
                placeholder="Assign to clients"
                value={searchAll}
                onChange={e => setSearchAll(e.target.value)}
                size="small"
                sx={{ minWidth: 220, background: '#f5f5f5', borderRadius: 1 }}
                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
                onFocus={() => {
                  if (!showAllClients && !justClosedRef.current) handleSearchClients();
                }}
              />
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          {/* Assigned Clients Table/List */}
          {loadingAssigned ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List>
              {pagedAssignedClients.length > 0 ? (
                pagedAssignedClients.map(client => (
                  <ListItem
                    key={client.id}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => handleUnassignClient(client.id)}>
                        <RemoveIcon sx={{ color: '#757575' }} />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={client.display_name || client.primary_contact?.display_name || 'Unknown Client'} />
                  </ListItem>
                ))
              ) : (
                <Typography sx={{ p: 2, color: '#666', textAlign: 'center' }}>
                  No clients assigned
                </Typography>
              )}
            </List>
          )}
          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
            <IconButton disabled={page === 1} onClick={() => setPage(page - 1)}>&lt;</IconButton>
            <Typography sx={{ mx: 2 }}>{page}</Typography>
            <IconButton disabled={page === totalPages} onClick={() => setPage(page + 1)}>&gt;</IconButton>
          </Box>
        </Paper>
        {/* Assign Clients Dialog */}
        <Dialog open={showAllClients} onClose={handleCloseAllClients} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Select Clients to Assign
            </Typography>
            <IconButton onClick={handleCloseAllClients}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {loadingAllClients ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List>
                {filteredAllClients.length > 0 ? (
                  filteredAllClients.map(client => (
                    <ListItem
                      key={client.id}
                      sx={{ py: 0.5, '&:hover': { backgroundColor: '#f5f5f5' } }}
                      secondaryAction={
                        <Checkbox
                          checked={selectedClients.includes(client.id)}
                          onChange={() => handleToggleClient(client.id)}
                          sx={{ mr: 1 }}
                        />
                      }
                    >
                      <ListItemText primary={client.display_name || client.primary_contact?.display_name || 'Unknown Client'} />
                    </ListItem>
                  ))
                ) : (
                  <Typography sx={{ p: 2, color: '#666', textAlign: 'center' }}>
                    No clients available to assign
                  </Typography>
                )}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAllClients} sx={{ color: '#666' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAssignClients}
              disabled={selectedClients.length === 0}
              sx={{ borderRadius: 1 }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button variant="contained" color="primary" sx={{ borderRadius: 1, px: 4 }} onClick={onClose}>
          Save
        </Button>
        <Button sx={{ color: '#1976d2', ml: 2 }} onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientAssignments;